import { useEffect, useMemo, useState } from "react";

export const DEFAULT_PAGE_SIZE = 10;

export interface Pagination<T> {
  /** Current 1-based page number (always clamped into `[1, pageCount]`). */
  page: number;
  /** Jump to an explicit page; the value is clamped on the next render. */
  setPage: (page: number) => void;
  /** Total number of pages (at least 1, even when there are 0 rows). */
  pageCount: number;
  /** The slice of `items` that belongs on the current page. */
  pageItems: T[];
  /** Total number of rows across all pages (i.e. `items.length`). */
  total: number;
  /** 1-based index of the first row shown (0 when empty). */
  from: number;
  /** 1-based index of the last row shown (0 when empty). */
  to: number;
  canPrev: boolean;
  canNext: boolean;
  next: () => void;
  prev: () => void;
}

/**
 * Generic client-side pagination over an already-filtered array. Slicing only —
 * the caller still owns fetching/filtering. The current page is clamped into
 * range on every render, so when the input list shrinks (e.g. a filter changes)
 * the user is never stranded on an empty page; they fall back to the last valid
 * page. To send the user back to page 1 when filters change, call `setPage(1)`
 * from the filter handlers (or key the component on the filter values).
 */
export function usePagination<T>(
  items: T[],
  pageSize: number = DEFAULT_PAGE_SIZE,
): Pagination<T> {
  const [page, setPage] = useState(1);

  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // Clamp the page into range whenever the list size or page changes. This
  // keeps a stale page index (left over after filtering) from showing an empty
  // table.
  const clampedPage = Math.min(Math.max(page, 1), pageCount);
  useEffect(() => {
    if (page !== clampedPage) setPage(clampedPage);
  }, [page, clampedPage]);

  const pageItems = useMemo(() => {
    const start = (clampedPage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, clampedPage, pageSize]);

  const from = total === 0 ? 0 : (clampedPage - 1) * pageSize + 1;
  const to = Math.min(clampedPage * pageSize, total);
  const canPrev = clampedPage > 1;
  const canNext = clampedPage < pageCount;

  return {
    page: clampedPage,
    setPage,
    pageCount,
    pageItems,
    total,
    from,
    to,
    canPrev,
    canNext,
    next: () => setPage(clampedPage + 1),
    prev: () => setPage(clampedPage - 1),
  };
}
